import type { ExerciseTemplate } from '@yuan-math/exercises';
import type { Figure } from '@yuan-math/curriculum';
import { fraction, gcd, rawFraction, toText } from '@yuan-math/math';
import {
  choiceExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * The rest of Grade 4.
 *
 * `arithmetic.ts`, `fractions.ts` and `decimals.ts` carry the spine Grade 4
 * shares with the grades either side of it. These templates are the year's own
 * work, and the year has a single character: arithmetic stops being a set of
 * facts and becomes a set of *methods*. A two-digit product is four small
 * products in a box; a long division is one division repeated down the places;
 * an angle is the first quantity a student measures with an instrument rather
 * than by counting.
 *
 * Two habits run through the file, inherited from `grade-three.ts`. Every
 * distractor is an error a Grade 4 teacher actually sees — the partial product
 * written without its zero, the protractor's other scale, the rectangle
 * credited with four lines of symmetry — rather than a plausible-looking
 * number. And no template asks for an answer whose units it has not put in the
 * prompt, because "8" and "8 cm" answer different questions.
 */

/** Names and objects for comparison problems, so the prose is not all "A" and "B". */
const CAST: { first: string; second: string; thing: string }[] = [
  { first: 'Maya', second: 'Ravi', thing: 'stickers' },
  { first: 'Omar', second: 'Lena', thing: 'marbles' },
  { first: 'Sofia', second: 'Kai', thing: 'trading cards' },
  { first: 'Noor', second: 'Ellis', thing: 'shells' },
  { first: 'Jonah', second: 'Priya', thing: 'buttons' },
  { first: 'Tomas', second: 'Aisha', thing: 'beads' },
];

/**
 * Names the size of one part: "one third", "five twelfths".
 *
 * Spelled out because `${d}th` renders as "one 3th" and "6 9ths", and a lesson
 * that cannot spell its own fractions is not one a parent will trust. Beyond
 * twelfths nobody says the word, so no template here goes above it.
 */
const PART_NAMES = [
  '', 'whole', 'half', 'third', 'quarter', 'fifth', 'sixth',
  'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
];
const partName = (denominator: number, count = 1): string => {
  const name = PART_NAMES[denominator] ?? `${denominator}th`;
  return count === 1 ? name : `${name}s`;
};

/** "1 piece", "4 pieces" — the difference an author notices and a generator forgets. */
const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "a rectangle", "an isosceles triangle", "an eighth". */
const article = (noun: string): string => (/^[aeiou]/i.test(noun) ? 'an' : 'a');
/** The same, where the article opens a sentence. */
const Article = (noun: string): string => (article(noun) === 'an' ? 'An' : 'A');

/** The numerators below `d` that leave the fraction already in lowest terms. */
const properNumerators = (d: number): number[] => {
  const out: number[] = [];
  for (let n = 1; n < d; n += 1) if (gcd(n, d) === 1) out.push(n);
  return out;
};

/** Every whole number that divides `value`, including 1 and itself. */
const factorsOf = (value: number): number[] => {
  const out: number[] = [];
  for (let i = 1; i <= value; i += 1) if (value % i === 0) out.push(i);
  return out;
};

const isPrime = (value: number): boolean => value > 1 && factorsOf(value).length === 2;

/** The primes and the odd composites that get mistaken for them. */
const PRIMES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59];
const ODD_COMPOSITES = [9, 15, 21, 25, 27, 33, 35, 39, 45, 49, 51, 55, 57];
const EVEN_COMPOSITES = [4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32];

/**
 * Conversions within one measurement system.
 *
 * `big` is always the larger unit, so a template never has to remember which
 * way round a pair is — it multiplies to go down and divides to go up.
 */
type Conversion = {
  /** How the units are written in a question: "km", "litres", "hours". */
  big: string;
  small: string;
  /** How they are said in an explanation: "one kilometre is 1000 metres". */
  bigOne: string;
  smallOne: string;
  factor: number;
  /** The factor a student reaches for instead, and what that mistake is called. */
  wrongFactor: number;
  wrongId: string;
};

const CONVERSIONS: Conversion[] = [
  { big: 'km', small: 'm', bigOne: 'kilometre', smallOne: 'metre', factor: 1000, wrongFactor: 100, wrongId: 'conversion-factor-wrong' },
  { big: 'm', small: 'cm', bigOne: 'metre', smallOne: 'centimetre', factor: 100, wrongFactor: 10, wrongId: 'conversion-factor-wrong' },
  { big: 'cm', small: 'mm', bigOne: 'centimetre', smallOne: 'millimetre', factor: 10, wrongFactor: 100, wrongId: 'conversion-factor-wrong' },
  { big: 'kg', small: 'g', bigOne: 'kilogram', smallOne: 'gram', factor: 1000, wrongFactor: 100, wrongId: 'conversion-factor-wrong' },
  { big: 'litres', small: 'ml', bigOne: 'litre', smallOne: 'millilitre', factor: 1000, wrongFactor: 100, wrongId: 'conversion-factor-wrong' },
  { big: 'hours', small: 'minutes', bigOne: 'hour', smallOne: 'minute', factor: 60, wrongFactor: 100, wrongId: 'time-treated-as-decimal' },
  { big: 'minutes', small: 'seconds', bigOne: 'minute', smallOne: 'second', factor: 60, wrongFactor: 100, wrongId: 'time-treated-as-decimal' },
];

type SymmetryShape = Extract<Figure, { kind: 'symmetry' }>['shape'];

/** Lines of symmetry, counted by folding rather than by counting sides. */
const SYMMETRY_SHAPES: { shape: SymmetryShape; name: string; lines: number; sides: number }[] = [
  { shape: 'square', name: 'square', lines: 4, sides: 4 },
  { shape: 'rectangle', name: 'rectangle', lines: 2, sides: 4 },
  { shape: 'equilateral-triangle', name: 'equilateral triangle', lines: 3, sides: 3 },
  { shape: 'isosceles-triangle', name: 'isosceles triangle', lines: 1, sides: 3 },
  { shape: 'scalene-triangle', name: 'scalene triangle', lines: 0, sides: 3 },
  { shape: 'parallelogram', name: 'parallelogram', lines: 0, sides: 4 },
  { shape: 'kite', name: 'kite', lines: 1, sides: 4 },
  { shape: 'regular-pentagon', name: 'regular pentagon', lines: 5, sides: 5 },
  { shape: 'regular-hexagon', name: 'regular hexagon', lines: 6, sides: 6 },
];

export const GRADE_FOUR_TEMPLATES: ExerciseTemplate[] = [
  /* --------------------------------------- multiplicative comparison ----- */
  template({
    id: 'times-as-many-product',
    conceptId: 'multiplicative-comparison',
    description: 'Find the larger quantity from a "times as many" comparison.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const base = rng.int(3, difficulty === 1 ? 8 : difficulty === 2 ? 14 : 25);
      const factor = rng.int(2, difficulty === 1 ? 5 : 9);
      const { first, second, thing } = rng.pick(CAST);
      return numericExercise({
        prompt: `${first} has ${base} ${thing}. ${second} has ${factor} times as many. How many ${thing} does ${second} have?`,
        value: base * factor,
        summary: `"${factor} times as many" means ${factor} lots of ${base}, which is ${base * factor}.`,
        hint: 'Draw one bar for the smaller amount, then that same bar repeated. How many bars altogether?',
        steps: [
          { label: 'Name the smaller amount.', latex: String(base) },
          { label: '"Times as many" is multiplication.', latex: `${factor} \\times ${base}` },
          { label: 'Multiply.', latex: `${factor} \\times ${base} = ${base * factor}` },
        ],
        traps: [trap(base + factor, 'comparison-read-as-additive')],
      });
    },
  }),

  template({
    id: 'times-as-many-factor',
    conceptId: 'multiplicative-comparison',
    description: 'Find the multiplier that relates two quantities.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const small = rng.int(3, difficulty === 2 ? 9 : 15);
      const factor = rng.int(2, difficulty === 2 ? 6 : 9);
      const big = small * factor;
      const { first, second, thing } = rng.pick(CAST);
      return numericExercise({
        prompt: `${first} has ${small} ${thing} and ${second} has ${big}. How many times as many does ${second} have?`,
        value: factor,
        summary: `${big} ÷ ${small} = ${factor}, so ${second} has ${factor} times as many.`,
        hint: 'How many of the smaller amount fit inside the larger one?',
        steps: [
          { label: 'The comparison is a division.', latex: `${big} \\div ${small}` },
          { label: 'Divide.', latex: `${big} \\div ${small} = ${factor}` },
          { label: 'Check by multiplying back.', latex: `${factor} \\times ${small} = ${big}` },
        ],
        traps: [trap(big - small, 'comparison-read-as-additive')],
      });
    },
  }),

  template({
    id: 'comparison-sentence-choice',
    conceptId: 'multiplicative-comparison',
    description: 'Choose the number sentence a "times as long" comparison describes.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const length = rng.int(4, difficulty === 3 ? 12 : 24);
      const factor = rng.int(3, 9);
      const { choices, correctId } = options(
        [
          { latex: `${factor} \\times ${length}`, correct: true },
          { latex: `${factor} + ${length}`, misconceptionId: 'comparison-read-as-additive' },
          { latex: `${length} - ${factor}`, misconceptionId: 'comparison-read-as-additive' },
          { latex: `${length} \\div ${factor}`, misconceptionId: 'divide-wrong-direction' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `A red ribbon is ${length} cm long. A blue ribbon is ${factor} times as long. Which calculation gives the blue ribbon's length?`,
        choices,
        correctId,
        summary: `"${factor} times as long" repeats the ${length} cm ribbon ${factor} times, which is ${factor} × ${length}.`,
        hint: 'Ask whether the sentence says how much *more*, or how many *times*.',
        steps: [
          { label: 'The red ribbon is one unit.', latex: `${length} \\text{ cm}` },
          { label: 'Blue is that unit, taken this many times.', latex: `${factor} \\times ${length} = ${factor * length}` },
        ],
      });
    },
  }),

  /* ---------------------------------------- multi-digit multiplication --- */
  template({
    id: 'two-digit-times-multiple-of-ten',
    conceptId: 'multiply-multi-digit',
    description: 'Multiply a two-digit number by a multiple of ten.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(difficulty === 2 ? 12 : 21, difficulty === 2 ? 39 : 89);
      const tens = rng.int(2, 9);
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a} \\times ${tens * 10}`,
        value: a * tens * 10,
        summary: `${a} × ${tens} = ${a * tens}, and there is a ten to put back, so the answer is ${a * tens * 10}.`,
        hint: `Multiply by ${tens} first, then by 10.`,
        steps: [
          { label: 'Split off the ten.', latex: `${a} \\times ${tens} \\times 10` },
          { label: 'Multiply by the digit.', latex: `${a} \\times ${tens} = ${a * tens}` },
          { label: 'Now by ten.', latex: `${a * tens} \\times 10 = ${a * tens * 10}` },
        ],
        traps: [trap(a * tens, 'trailing-zeros-dropped')],
      });
    },
  }),

  template({
    id: 'two-digit-times-two-digit',
    conceptId: 'multiply-multi-digit',
    description: 'Multiply two two-digit numbers using partial products.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      // Both ones digits are non-zero: a zero there erases two of the four
      // partial products, and with them the whole point of the method.
      const aTens = rng.int(difficulty === 3 ? 1 : 2, difficulty === 5 ? 8 : 5);
      const aOnes = rng.int(1, 9);
      const bTens = rng.int(difficulty === 3 ? 1 : 2, difficulty === 5 ? 8 : 5);
      const bOnes = rng.int(1, 9);
      const a = aTens * 10 + aOnes;
      const b = bTens * 10 + bOnes;

      // The tens digit of b treated as though it were a ones digit.
      const unshifted = a * bOnes + a * bTens;
      // Only the two "matching" products kept: tens × tens and ones × ones.
      const crossOnly = aTens * 10 * bTens * 10 + aOnes * bOnes;

      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a} \\times ${b}`,
        value: a * b,
        summary: `${a} × ${bTens * 10} = ${a * bTens * 10} and ${a} × ${bOnes} = ${a * bOnes}; together that is ${a * b}.`,
        hint: `Split ${b} into ${bTens * 10} and ${bOnes}, and multiply ${a} by each piece.`,
        steps: [
          { label: 'Split the second factor.', latex: `${a} \\times (${bTens * 10} + ${bOnes})` },
          { label: 'Multiply by the tens.', latex: `${a} \\times ${bTens * 10} = ${a * bTens * 10}`, note: 'The ten is why this product ends in a zero.' },
          { label: 'Multiply by the ones.', latex: `${a} \\times ${bOnes} = ${a * bOnes}` },
          { label: 'Add the partial products.', latex: `${a * bTens * 10} + ${a * bOnes} = ${a * b}` },
        ],
        traps: [
          trap(unshifted, 'partial-product-not-shifted'),
          // Only offered when it is a different number, so one wrong answer
          // never carries two diagnoses.
          ...(crossOnly !== unshifted ? [trap(crossOnly, 'partial-product-dropped')] : []),
        ],
      });
    },
  }),

  template({
    id: 'missing-partial-product',
    conceptId: 'multiply-multi-digit',
    description: 'Supply the missing entry in a partial-product decomposition.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const aTens = rng.int(1, difficulty === 4 ? 5 : 8);
      const aOnes = rng.int(2, 9);
      const bTens = rng.int(1, difficulty === 4 ? 5 : 8);
      const bOnes = rng.int(2, 9);
      const a = aTens * 10 + aOnes;
      const b = bTens * 10 + bOnes;
      const parts = [
        { value: aTens * 10 * (bTens * 10), unshifted: aTens * bTens },
        { value: aTens * 10 * bOnes, unshifted: aTens * bOnes },
        { value: aOnes * (bTens * 10), unshifted: aOnes * bTens },
        { value: aOnes * bOnes, unshifted: aOnes * bOnes },
      ];
      const hidden = rng.int(0, 2); // never the ones × ones product, which needs no shift
      const shown = parts.map((part, i) => (i === hidden ? '\\square' : String(part.value)));
      const missing = parts[hidden] as { value: number; unshifted: number };

      return numericExercise({
        prompt: `These are the four partial products of ${a} × ${b}. What belongs in the box?`,
        latex: `${a} \\times ${b} = ${shown.join(' + ')}`,
        value: missing.value,
        summary: `The missing product is ${missing.value}; the four together make ${a * b}.`,
        hint: 'A digit in the tens column is worth ten times what it looks like.',
        steps: [
          { label: 'Write both numbers in place value.', latex: `(${aTens * 10} + ${aOnes}) \\times (${bTens * 10} + ${bOnes})` },
          { label: 'Every piece meets every piece.', latex: `${parts.map((part) => part.value).join(' + ')}` },
          { label: 'And they add to the product.', latex: `${a} \\times ${b} = ${a * b}` },
        ],
        traps: [trap(missing.unshifted, 'partial-product-not-shifted')],
      });
    },
  }),

  /* -------------------------------------------------- long division ------ */
  template({
    id: 'long-division-exact',
    conceptId: 'long-division',
    description: 'Divide a three-digit number by a single digit, with no remainder.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(3, difficulty === 2 ? 6 : 9);
      // The quotient is chosen first so the division always comes out exactly.
      const quotient = difficulty === 2 ? rng.int(12, 89) : rng.int(112, 989);
      const dividend = quotient * divisor;
      const digitsWithoutZero = String(quotient).replace(/0/g, '');
      return numericExercise({
        prompt: 'Divide.',
        latex: `${dividend} \\div ${divisor}`,
        value: quotient,
        summary: `${divisor} × ${quotient} = ${dividend}, so the quotient is ${quotient}.`,
        hint: 'Work one place at a time, from the left, carrying what is left over into the next place.',
        steps: [
          { label: 'Start at the leading place.', latex: `${dividend} \\div ${divisor}` },
          { label: 'Divide each place in turn.', latex: `\\text{quotient} = ${quotient}` },
          { label: 'Check by multiplying back.', latex: `${quotient} \\times ${divisor} = ${dividend}` },
        ],
        traps:
          // Only when a zero really is at risk of being dropped: the trap is a
          // diagnosis, and a diagnosis of an error the student cannot make is
          // noise.
          digitsWithoutZero !== String(quotient) && digitsWithoutZero !== ''
            ? [trap(Number(digitsWithoutZero), 'quotient-zero-skipped')]
            : [],
      });
    },
  }),

  template({
    id: 'long-division-internal-zero',
    conceptId: 'long-division',
    description: 'Divide where a place will not divide once, so the quotient needs a zero.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(3, difficulty === 4 ? 7 : 9);
      const hundreds = rng.int(1, 9);
      const units = rng.int(1, 9);
      const quotient = hundreds * 100 + units; // the tens digit is a deliberate zero
      const dividend = quotient * divisor;
      return numericExercise({
        prompt: 'Divide. Watch the tens place.',
        latex: `${dividend} \\div ${divisor}`,
        value: quotient,
        summary: `The tens place will not take ${divisor} even once, so a 0 goes in the quotient there: the answer is ${quotient}.`,
        hint: 'If a place divides zero times, that zero still has to be written down.',
        steps: [
          { label: 'Divide the hundreds.', latex: `\\text{hundreds digit} = ${hundreds}` },
          { label: 'The tens will not divide once.', latex: '\\text{tens digit} = 0', note: 'Write the zero and carry on.' },
          { label: 'Divide what is left.', latex: `\\text{ones digit} = ${units}` },
          { label: 'Check by multiplying back.', latex: `${quotient} \\times ${divisor} = ${dividend}` },
        ],
        traps: [trap(hundreds * 10 + units, 'quotient-zero-skipped')],
      });
    },
  }),

  template({
    id: 'long-division-remainder-value',
    conceptId: 'long-division',
    description: 'State what is left over after dividing.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(3, difficulty === 3 ? 7 : 9);
      const quotient = rng.int(23, difficulty === 3 ? 98 : 489);
      const remainder = rng.int(1, divisor - 1);
      const dividend = quotient * divisor + remainder;
      return numericExercise({
        prompt: 'Divide, and give the remainder only.',
        latex: `${dividend} \\div ${divisor}`,
        value: remainder,
        summary: `${divisor} × ${quotient} = ${quotient * divisor}, and ${dividend} − ${quotient * divisor} = ${remainder} is left over.`,
        hint: `The remainder is always smaller than ${divisor}. If yours is not, you stopped a round too early.`,
        steps: [
          { label: 'Divide as far as it will go.', latex: `${dividend} \\div ${divisor} = ${quotient} \\text{ r } ${remainder}` },
          { label: 'The remainder is what did not fit.', latex: String(remainder) },
          { label: 'Check.', latex: `${quotient} \\times ${divisor} + ${remainder} = ${dividend}` },
        ],
        traps: [trap(0, 'ignored-remainder')],
      });
    },
  }),

  template({
    id: 'division-rounds-up-in-context',
    conceptId: 'long-division',
    description: 'Interpret a remainder: how many containers are actually needed.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const perBox = rng.int(4, difficulty === 4 ? 8 : 12);
      const boxes = rng.int(11, difficulty === 4 ? 40 : 90);
      const leftOver = rng.int(1, perBox - 1);
      const total = boxes * perBox + leftOver;
      return numericExercise({
        prompt: `A packer puts ${perBox} apples in each crate. There are ${total} apples. How many crates are needed so that every apple is packed?`,
        value: boxes + 1,
        unit: 'crates',
        summary: `${total} ÷ ${perBox} = ${boxes} remainder ${leftOver}. Those ${leftOver} apples still need a crate, so ${boxes + 1} crates are needed.`,
        hint: 'Do the division first, then ask what the leftover apples need.',
        steps: [
          { label: 'Divide.', latex: `${total} \\div ${perBox} = ${boxes} \\text{ r } ${leftOver}` },
          { label: 'Full crates.', latex: String(boxes) },
          { label: 'The leftovers need one more.', latex: `${boxes} + 1 = ${boxes + 1}` },
        ],
        traps: [trap(boxes, 'ignored-remainder'), trap(leftOver, 'ignored-remainder')],
      });
    },
  }),

  /* ------------------------------------------ prime and composite -------- */
  template({
    id: 'classify-prime-composite',
    conceptId: 'prime-composite',
    description: 'Decide whether a number is prime, composite, or neither.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const pool =
        difficulty === 1
          ? [...PRIMES.slice(0, 6), ...EVEN_COMPOSITES.slice(0, 8), 1]
          : difficulty === 2
            ? [...PRIMES.slice(0, 10), ...ODD_COMPOSITES.slice(0, 6), ...EVEN_COMPOSITES, 1]
            : [...PRIMES, ...ODD_COMPOSITES, ...EVEN_COMPOSITES, 1];
      const value = rng.pick(pool);
      const prime = isPrime(value);
      const neither = value === 1;
      const factors = factorsOf(value);

      /*
       * Only the distractors a named misunderstanding actually produces are
       * tagged. Calling 1 prime is the "no factors but itself" error; calling
       * an odd composite prime is the parity error. Calling 4 prime is neither
       * of those, and inventing a diagnosis for it would teach the student to
       * distrust every other one.
       */
      const callingItPrime = neither ? 'one-counted-as-prime' : value % 2 === 1 ? 'odd-assumed-prime' : undefined;
      const callingItComposite = prime && value === 2 ? 'odd-assumed-prime' : undefined;
      const { choices, correctId } = options(
        [
          { label: 'Prime', correct: prime, ...(!prime && callingItPrime ? { misconceptionId: callingItPrime } : {}) },
          {
            label: 'Composite',
            correct: !prime && !neither,
            ...(prime && callingItComposite ? { misconceptionId: callingItComposite } : {}),
          },
          { label: 'Neither', correct: neither },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Is ${value} prime, composite, or neither?`,
        choices,
        correctId,
        summary: neither
          ? '1 has only one factor — itself — so it is neither prime nor composite.'
          : prime
            ? `${value} has exactly two factors, 1 and ${value}, so it is prime.`
            : `${value} has ${factors.length} factors: ${factors.join(', ')}. More than two makes it composite.`,
        hint: 'Count the factors. Exactly two means prime; more than two means composite.',
        steps: [
          { label: 'List the factors.', latex: factors.join(', ') },
          { label: 'Count them.', latex: String(factors.length) },
          {
            label: neither ? 'One factor: neither.' : prime ? 'Two factors: prime.' : 'More than two: composite.',
            latex: String(value),
          },
        ],
      });
    },
  }),

  template({
    id: 'find-the-prime',
    conceptId: 'prime-composite',
    description: 'Pick the prime out of a set that includes odd composites.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const prime = rng.pick(difficulty === 2 ? PRIMES.slice(0, 8) : PRIMES);
      const shuffled = rng.shuffle(ODD_COMPOSITES);
      const [oddOne, oddTwo] = [shuffled[0] ?? 9, shuffled[1] ?? 15];
      const other = difficulty === 4 ? 1 : rng.pick(EVEN_COMPOSITES);
      // The smallest factor above 1 — what the student should have tested for.
      const witness = factorsOf(oddOne)[1] ?? 3;
      const { choices, correctId } = options(
        [
          { label: String(prime), correct: true },
          { label: String(oddOne), misconceptionId: 'odd-assumed-prime' },
          { label: String(oddTwo), misconceptionId: 'odd-assumed-prime' },
          { label: String(other), misconceptionId: other === 1 ? 'one-counted-as-prime' : 'odd-assumed-prime' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which of these numbers is prime?',
        choices,
        correctId,
        summary: `${prime} has no factors besides 1 and itself. ${oddOne} and ${oddTwo} are odd, but odd is not the same as prime.`,
        hint: 'Try dividing each one by 3, 5 and 7 before you decide.',
        steps: [
          { label: 'Test the odd ones too.', latex: `${oddOne} = ${witness} \\times ${oddOne / witness}` },
          { label: 'The prime has no such pair.', latex: String(prime) },
        ],
      });
    },
  }),

  template({
    id: 'count-primes-in-range',
    conceptId: 'prime-composite',
    description: 'Count the primes in a short run of numbers.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const from = rng.int(1, difficulty === 3 ? 20 : 45);
      const to = from + rng.int(9, difficulty === 3 ? 14 : 20);
      const primes: number[] = [];
      const odds: number[] = [];
      for (let n = from; n <= to; n += 1) {
        if (isPrime(n)) primes.push(n);
        if (n % 2 === 1) odds.push(n);
      }
      const withOne = from === 1 ? primes.length + 1 : primes.length;
      return numericExercise({
        prompt: `How many prime numbers are there from ${from} to ${to}, including both ends?`,
        value: primes.length,
        summary: `The primes in that run are ${primes.join(', ')} — ${primes.length} of them.`,
        hint: 'Cross out every multiple of 2, then of 3, then of 5. What survives is prime.',
        steps: [
          { label: 'Cross out the multiples.', latex: `${from} \\dots ${to}` },
          { label: 'What is left.', latex: primes.join(', ') },
          { label: 'Count them.', latex: String(primes.length) },
        ],
        traps: [
          ...(withOne !== primes.length ? [trap(withOne, 'one-counted-as-prime')] : []),
          ...(odds.length !== primes.length && odds.length !== withOne
            ? [trap(odds.length, 'odd-assumed-prime')]
            : []),
        ],
      });
    },
  }),

  /* ------------------------------------ fractions with like denominators - */
  template({
    id: 'add-like-denominators',
    conceptId: 'add-like-fractions',
    description: 'Add two fractions that already share a denominator.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.int(3, difficulty === 1 ? 6 : difficulty === 2 ? 10 : 12);
      const a = rng.int(1, d - 2);
      // Difficulty 3 allows the sum to pass the whole, which is the step most
      // students find surprising: 5/6 + 4/6 is more than one, and still sixths.
      const b = difficulty === 3 ? rng.int(1, d - 1) : rng.int(1, d - 1 - a);
      const sum = a + b;
      return fractionExercise({
        prompt: 'Add. The pieces are already the same size.',
        latex: `\\frac{${a}}{${d}} + \\frac{${b}}{${d}}`,
        value: rawFraction(sum, d),
        summary: `${count(a, partName(d))} and ${b} ${partName(d, b)} make ${count(sum, partName(d))}. The size of a piece never changed, so the denominator did not either.`,
        hint: 'Count the pieces on top. The bottom number says how big a piece is, and that has not changed.',
        steps: [
          { label: 'The pieces match, so count them.', latex: `${a} + ${b} = ${sum}` },
          { label: 'They are still the same size.', latex: `\\frac{${sum}}{${d}}` },
        ],
        traps: [fractionTrap(rawFraction(sum, d * 2), 'denominator-added-with-numerator')],
      });
    },
  }),

  template({
    id: 'subtract-like-denominators',
    conceptId: 'add-like-fractions',
    description: 'Subtract two fractions that already share a denominator.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.int(4, difficulty === 2 ? 8 : 12);
      const a = rng.int(3, d);
      const b = rng.int(1, a - 2 > 0 ? a - 2 : 1);
      const difference = a - b;
      return fractionExercise({
        prompt: 'Subtract.',
        latex: `\\frac{${a}}{${d}} - \\frac{${b}}{${d}}`,
        value: rawFraction(difference, d),
        summary: `Take ${count(b, 'piece')} away from ${count(a, 'piece')} and ${difference} of them are left — still ${partName(d, difference)}.`,
        hint: 'Only the count changes. The pieces are the same size before and after.',
        steps: [
          { label: 'Subtract the counts.', latex: `${a} - ${b} = ${difference}` },
          { label: 'The pieces are unchanged.', latex: `\\frac{${difference}}{${d}}` },
        ],
        traps: [fractionTrap(rawFraction(difference, d * 2), 'denominator-added-with-numerator')],
      });
    },
  }),

  template({
    id: 'decompose-like-fraction',
    conceptId: 'add-like-fractions',
    description: 'Find the missing numerator in a fraction decomposition.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(4, difficulty === 2 ? 8 : 12);
      const total = rng.int(3, d);
      const known = rng.int(1, total - 1);
      const missing = total - known;
      return numericExercise({
        prompt: 'What numerator belongs in the box?',
        latex: `\\frac{${known}}{${d}} + \\frac{\\square}{${d}} = \\frac{${total}}{${d}}`,
        value: missing,
        summary: `${count(known, 'piece')} plus ${count(missing, 'piece')} makes ${count(total, 'piece')}, so the box holds ${missing}.`,
        hint: 'The denominators are the same throughout — this is a question about counts.',
        steps: [
          { label: 'Only the top numbers are in play.', latex: `${known} + \\square = ${total}` },
          { label: 'So the box is the difference.', latex: `${total} - ${known} = ${missing}` },
        ],
        traps: [trap(total + known, 'denominator-added-with-numerator')],
      });
    },
  }),

  template({
    id: 'add-like-fractions-and-simplify',
    conceptId: 'add-like-fractions',
    description: 'Add fractions with a shared denominator, then write the answer in lowest terms.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.pick(difficulty === 3 ? [4, 6, 8] : [6, 8, 9, 10, 12]);
      // Chosen so the sum really does share a factor with the denominator —
      // otherwise "simplify" is an instruction with nothing behind it.
      const candidates: number[] = [];
      for (let total = 2; total < d; total += 1) if (gcd(total, d) > 1) candidates.push(total);
      const sum = rng.pick(candidates.length > 0 ? candidates : [2]);
      const a = rng.int(1, sum - 1);
      const b = sum - a;
      return fractionExercise({
        prompt: 'Add, and write your answer in its simplest form.',
        latex: `\\frac{${a}}{${d}} + \\frac{${b}}{${d}}`,
        value: fraction(sum, d),
        requireSimplified: true,
        summary: `${a} + ${b} = ${sum}, giving ${sum}/${d} — which simplifies to ${toText(fraction(sum, d))}.`,
        hint: 'Add first. Then ask whether the top and the bottom share a factor.',
        steps: [
          { label: 'Add the counts.', latex: `\\frac{${a} + ${b}}{${d}} = \\frac{${sum}}{${d}}` },
          { label: 'Divide top and bottom by their common factor.', latex: toText(fraction(sum, d)) },
        ],
        traps: [fractionTrap(rawFraction(sum, d * 2), 'denominator-added-with-numerator')],
      });
    },
  }),

  /* --------------------------------- a fraction times a whole number ----- */
  template({
    id: 'whole-times-fraction',
    conceptId: 'fraction-times-whole',
    description: 'Multiply a unit or proper fraction by a whole number.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.int(3, difficulty === 2 ? 8 : 12);
      // In lowest terms: "2/4 of a litre" is a correct question badly asked,
      // and it invites the student to simplify when that is not the skill here.
      const n = rng.pick(properNumerators(d));
      const k = rng.int(2, difficulty === 2 ? 5 : 9);
      return fractionExercise({
        prompt: 'Multiply.',
        latex: `${k} \\times \\frac{${n}}{${d}}`,
        value: rawFraction(k * n, d),
        summary: `${k} lots of ${n}/${d} is ${k} × ${n} = ${k * n} pieces, each still ${article(partName(d))} ${partName(d)}.`,
        hint: `Write it out as ${n}/${d} added to itself ${k} times, and count the pieces.`,
        steps: [
          { label: 'Repeated addition.', latex: `\\underbrace{\\frac{${n}}{${d}} + \\dots + \\frac{${n}}{${d}}}_{${k}}` },
          { label: 'Only the count grows.', latex: `\\frac{${k} \\times ${n}}{${d}} = \\frac{${k * n}}{${d}}` },
        ],
        traps: [fractionTrap(rawFraction(k * n, k * d), 'whole-multiplied-into-denominator')],
      });
    },
  }),

  template({
    id: 'fraction-of-an-amount',
    conceptId: 'fraction-times-whole',
    description: 'Find a fraction of a whole quantity.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(3, difficulty === 3 ? 6 : 10);
      const n = rng.pick(properNumerators(d));
      const groups = rng.int(2, difficulty === 3 ? 8 : 15);
      const whole = d * groups;
      return numericExercise({
        prompt: `A box holds ${whole} pencils. How many pencils are ${n}/${d} of them?`,
        value: n * groups,
        unit: 'pencils',
        summary: `One ${partName(d)} of ${whole} is ${groups}, so ${n} of them is ${n} × ${groups} = ${n * groups}.`,
        hint: 'Divide by the bottom number to find one piece, then multiply by the top number.',
        steps: [
          { label: 'Find one part.', latex: `${whole} \\div ${d} = ${groups}` },
          { label: 'Take the number of parts asked for.', latex: `${n} \\times ${groups} = ${n * groups}` },
        ],
        traps: [trap(whole * n, 'multiplication-always-grows')],
      });
    },
  }),

  template({
    id: 'fraction-times-whole-in-context',
    conceptId: 'fraction-times-whole',
    description: 'Multiply a fraction by a whole number in a measurement setting.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(3, difficulty === 3 ? 6 : 10);
      const n = rng.pick(properNumerators(d));
      const jugs = rng.int(2, difficulty === 3 ? 6 : 11);
      return fractionExercise({
        prompt: `Each jug holds ${n}/${d} of a litre. How many litres do ${jugs} jugs hold altogether?`,
        value: rawFraction(n * jugs, d),
        summary: `${jugs} jugs of ${n}/${d} litre each is ${jugs} × ${n} = ${n * jugs} ${partName(d, n * jugs)} of a litre.`,
        hint: 'The jugs are all the same size, so this is repeated addition.',
        steps: [
          { label: 'Same amount, repeated.', latex: `${jugs} \\times \\frac{${n}}{${d}}` },
          { label: 'Multiply the count of pieces.', latex: `\\frac{${jugs * n}}{${d}}` },
        ],
        traps: [fractionTrap(rawFraction(n * jugs, d * jugs), 'whole-multiplied-into-denominator')],
      });
    },
  }),

  /* ------------------------------------------ fractions and decimals ----- */
  template({
    id: 'fraction-to-decimal',
    conceptId: 'fractions-to-decimals',
    description: 'Write a fraction in tenths or hundredths as a decimal.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const tenths = difficulty === 1;
      const denominator = tenths ? 10 : 100;
      // Hundredths avoid multiples of ten, so the question is never really a
      // tenths question wearing two digits.
      const numerator = tenths
        ? rng.int(1, 9)
        : difficulty === 2
          ? rng.int(1, 9)
          : rng.pick(Array.from({ length: 89 }, (_, i) => i + 11).filter((n) => n % 10 !== 0));
      return numericExercise({
        prompt: 'Write this fraction as a decimal.',
        latex: `\\frac{${numerator}}{${denominator}}`,
        value: numerator / denominator,
        summary: tenths
          ? `${numerator} tenths is ${numerator} in the first column after the point.`
          : `${numerator} hundredths fills the first two columns after the point: ${numerator / denominator}.`,
        hint: 'The denominator picks the column. Tenths are one place after the point, hundredths are two.',
        steps: [
          { label: 'Name the unit.', latex: `\\frac{1}{${denominator}}` },
          { label: 'Count that many of them.', latex: `\\frac{${numerator}}{${denominator}} = ${numerator / denominator}` },
        ],
        traps: tenths ? [] : [trap(numerator / 10, 'fraction-to-decimal-place-lost')],
      });
    },
  }),

  template({
    id: 'decimal-to-fraction',
    conceptId: 'fractions-to-decimals',
    description: 'Write a decimal as a fraction in tenths or hundredths.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const denominator = difficulty === 2 ? 10 : 100;
      const numerator =
        denominator === 10
          ? rng.int(1, 9)
          : rng.pick(Array.from({ length: 98 }, (_, i) => i + 1).filter((n) => n % 10 !== 0));
      const decimal = numerator / denominator;
      return fractionExercise({
        prompt: `Write ${decimal} as a fraction with a denominator of ${denominator}.`,
        value: rawFraction(numerator, denominator),
        requireExactForm: true,
        summary: `${decimal} is ${numerator} ${denominator === 10 ? 'tenths' : 'hundredths'}, which is ${numerator}/${denominator}.`,
        hint: 'Read the last digit of the decimal: which column is it in?',
        steps: [
          { label: 'Name the last column.', latex: `\\frac{1}{${denominator}}` },
          { label: 'Count how many.', latex: `\\frac{${numerator}}{${denominator}}` },
        ],
        traps: [
          ...(denominator === 100
            ? [fractionTrap(rawFraction(numerator, 10), 'fraction-to-decimal-place-lost')]
            : []),
        ],
      });
    },
  }),

  template({
    id: 'tenths-plus-hundredths',
    conceptId: 'fractions-to-decimals',
    description: 'Add a number of tenths to a number of hundredths, as a decimal.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const tenths = rng.int(1, difficulty === 3 ? 5 : 9);
      const hundredths = rng.pick(
        Array.from({ length: difficulty === 3 ? 40 : 89 }, (_, i) => i + 10).filter((n) => n % 10 !== 0),
      );
      const total = (tenths * 10 + hundredths) / 100;
      return numericExercise({
        prompt: 'Add, and give your answer as a decimal.',
        latex: `\\frac{${tenths}}{10} + \\frac{${hundredths}}{100}`,
        value: total,
        summary: `${tenths} tenths is ${tenths * 10} hundredths, so the total is ${tenths * 10 + hundredths} hundredths — ${total}.`,
        hint: 'Rewrite the tenths as hundredths first. One tenth is ten hundredths.',
        steps: [
          { label: 'Make the pieces the same size.', latex: `\\frac{${tenths}}{10} = \\frac{${tenths * 10}}{100}` },
          { label: 'Now add.', latex: `\\frac{${tenths * 10} + ${hundredths}}{100} = \\frac{${tenths * 10 + hundredths}}{100}` },
          { label: 'And write it as a decimal.', latex: String(total) },
        ],
        traps: [trap((tenths + hundredths) / 100, 'fraction-to-decimal-place-lost')],
      });
    },
  }),

  /* ------------------------------------------------ unit conversion ------ */
  template({
    id: 'convert-to-smaller-unit',
    conceptId: 'unit-conversion',
    description: 'Convert a measurement into a smaller unit.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const conversion = rng.pick(CONVERSIONS);
      const amount = rng.int(2, difficulty === 1 ? 9 : difficulty === 2 ? 24 : 60);
      const value = amount * conversion.factor;
      return numericExercise({
        prompt: `Convert ${amount} ${conversion.big} into ${conversion.small}.`,
        value,
        unit: conversion.small,
        summary: `One ${conversion.bigOne} is ${conversion.factor} ${conversion.smallOne}s, so ${amount} of them is ${value}.`,
        hint: `The ${conversion.smallOne} is the smaller unit, so there will be more of them — this is a multiplication.`,
        steps: [
          { label: 'State the conversion.', latex: `1 \\text{ ${conversion.big}} = ${conversion.factor} \\text{ ${conversion.small}}` },
          { label: 'Multiply.', latex: `${amount} \\times ${conversion.factor} = ${value}` },
        ],
        traps: [trap(amount * conversion.wrongFactor, conversion.wrongId)],
      });
    },
  }),

  template({
    id: 'convert-to-larger-unit',
    conceptId: 'unit-conversion',
    description: 'Convert a measurement into a larger unit.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const conversion = rng.pick(CONVERSIONS);
      const amount = rng.int(2, difficulty === 2 ? 9 : difficulty === 3 ? 30 : 75);
      const small = amount * conversion.factor;
      return numericExercise({
        prompt: `Convert ${small} ${conversion.small} into ${conversion.big}.`,
        value: amount,
        unit: conversion.big,
        summary: `Every ${conversion.factor} ${conversion.smallOne}s make one ${conversion.bigOne}, and ${small} ÷ ${conversion.factor} = ${amount}.`,
        hint: `The ${conversion.bigOne} is the larger unit, so there will be fewer of them — this is a division.`,
        steps: [
          { label: 'State the conversion.', latex: `1 \\text{ ${conversion.big}} = ${conversion.factor} \\text{ ${conversion.small}}` },
          { label: 'Divide.', latex: `${small} \\div ${conversion.factor} = ${amount}` },
        ],
        traps: [trap(small * conversion.factor, 'conversion-inverted')],
      });
    },
  }),

  template({
    id: 'convert-mixed-measurement',
    conceptId: 'unit-conversion',
    description: 'Rewrite a measurement given in two units as a single number.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const conversion = rng.pick(CONVERSIONS.filter((entry) => entry.factor <= 100));
      const whole = rng.int(2, difficulty === 3 ? 6 : 15);
      const extra = rng.int(1, conversion.factor - 1);
      const value = whole * conversion.factor + extra;
      return numericExercise({
        prompt: `A measurement is ${whole} ${conversion.big} and ${extra} ${conversion.small}. Write it in ${conversion.small} only.`,
        value,
        unit: conversion.small,
        summary: `${whole} ${conversion.big} is ${whole * conversion.factor} ${conversion.small}, and ${extra} more makes ${value}.`,
        hint: 'Convert the larger unit first, then add on what is left.',
        steps: [
          { label: 'Convert the whole units.', latex: `${whole} \\times ${conversion.factor} = ${whole * conversion.factor}` },
          { label: 'Add the remainder.', latex: `${whole * conversion.factor} + ${extra} = ${value}` },
        ],
        traps: [trap(whole + extra, 'measurement-units-mixed')],
      });
    },
  }),

  /* -------------------------------------------------- measuring angles --- */
  template({
    id: 'read-the-protractor',
    conceptId: 'angle-measure',
    description: 'Read an angle from a protractor.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      // 90° is excluded: the two scales agree there, so the question could not
      // tell a correct reading apart from the classic wrong one.
      const choicesForDifficulty =
        difficulty === 1
          ? [20, 30, 40, 50, 60, 70, 80]
          : difficulty === 2
            ? [100, 110, 120, 130, 140, 150, 160]
            : [15, 25, 35, 45, 55, 65, 75, 85, 95, 105, 115, 125, 135, 145, 155, 165];
      const degrees = rng.pick(choicesForDifficulty);
      return numericExercise({
        prompt: 'What does this angle measure?',
        figure: { kind: 'angle', degrees, protractor: true },
        value: degrees,
        unit: '°',
        summary: `Following the scale that starts at 0 along the lower arm, the second arm crosses at ${degrees}°.`,
        hint: 'Find the scale whose 0 sits on one arm of the angle, and read round from there.',
        steps: [
          { label: 'Put the centre of the protractor on the vertex.', latex: '\\text{vertex}' },
          { label: 'Find the scale that reads 0 along one arm.', latex: '0^\\circ' },
          { label: 'Read where the other arm crosses.', latex: `${degrees}^\\circ` },
        ],
        traps: [trap(180 - degrees, 'protractor-scale-misread')],
      });
    },
  }),

  template({
    id: 'classify-the-angle',
    conceptId: 'angle-measure',
    description: 'Name an angle as acute, right, obtuse or straight.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const degrees = rng.pick(
        difficulty === 1
          ? [30, 45, 60, 90, 120, 135, 150, 180]
          : [10, 25, 35, 55, 65, 80, 85, 90, 95, 100, 115, 125, 140, 155, 170, 180],
      );
      const name = degrees === 90 ? 'Right' : degrees === 180 ? 'Straight' : degrees < 90 ? 'Acute' : 'Obtuse';
      // Reading the other protractor scale turns an acute angle obtuse and an
      // obtuse one acute — but it does nothing to 90° or 180°, so those two
      // wrong answers are left undiagnosed rather than misdiagnosed.
      const misread = name === 'Acute' ? 'Obtuse' : name === 'Obtuse' ? 'Acute' : '';
      const { choices, correctId } = options(
        [
          { label: 'Acute', correct: name === 'Acute', ...(misread === 'Acute' ? { misconceptionId: 'protractor-scale-misread' } : {}) },
          { label: 'Right', correct: name === 'Right' },
          { label: 'Obtuse', correct: name === 'Obtuse', ...(misread === 'Obtuse' ? { misconceptionId: 'protractor-scale-misread' } : {}) },
          { label: 'Straight', correct: name === 'Straight' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'What kind of angle is this?',
        figure: { kind: 'angle', degrees },
        choices,
        correctId,
        summary: `It measures ${degrees}°, and ${degrees === 90 ? 'exactly 90° is a right angle' : degrees === 180 ? '180° is a straight angle' : degrees < 90 ? 'under 90° is acute' : 'between 90° and 180° is obtuse'}.`,
        hint: 'Compare it with the corner of a page. Is the opening smaller or larger than that?',
        steps: [
          { label: 'Compare with a right angle.', latex: '90^\\circ' },
          { label: 'This angle.', latex: `${degrees}^\\circ` },
        ],
      });
    },
  }),

  template({
    id: 'angle-split-into-parts',
    conceptId: 'angle-measure',
    description: 'Find the second part of an angle cut in two by a ray.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const whole = rng.pick(
        difficulty === 3 ? [60, 70, 80, 90, 100, 110, 120] : [95, 105, 115, 125, 135, 145, 155, 165, 175],
      );
      const part = rng.int(2, Math.floor(whole / 5) - 2) * 5;
      const other = whole - part;
      return numericExercise({
        prompt: `The whole angle measures ${whole}°. The dashed ray cuts off ${part}° from the lower arm. How large is the other part?`,
        figure: { kind: 'angle', degrees: whole, split: part },
        value: other,
        unit: '°',
        summary: `The two parts make up the whole, so the second one is ${whole} − ${part} = ${other}°.`,
        hint: 'The two pieces together are the whole angle. Which operation undoes "together"?',
        steps: [
          { label: 'The parts add to the whole.', latex: `${part} + \\square = ${whole}` },
          { label: 'So subtract.', latex: `${whole} - ${part} = ${other}` },
        ],
        traps: [trap(whole + part, 'angle-parts-not-added'), trap(part, 'angle-parts-not-added')],
      });
    },
  }),

  /* -------------------------------------------------------- symmetry ----- */
  template({
    id: 'count-lines-of-symmetry',
    conceptId: 'symmetry',
    description: 'Count the lines of symmetry of a named shape.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const pool =
        difficulty === 1
          ? SYMMETRY_SHAPES.filter((entry) => entry.lines > 0)
          : difficulty === 2
            ? SYMMETRY_SHAPES
            : SYMMETRY_SHAPES.filter((entry) => entry.lines !== entry.sides);
      const shape = rng.pick(pool);
      // The two errors this shape can actually provoke, and only those.
      const diagonalsCounted = shape.lines + (shape.name === 'rectangle' ? 2 : shape.name === 'parallelogram' ? 2 : shape.name === 'kite' ? 1 : 0);
      return numericExercise({
        prompt: `How many lines of symmetry does this ${shape.name} have?`,
        figure: { kind: 'symmetry', shape: shape.shape, label: shape.name },
        value: shape.lines,
        summary:
          shape.lines === 0
            ? `${Article(shape.name)} ${shape.name} has no lines of symmetry: no fold leaves it looking the same.`
            : shape.lines === 1
              ? `${Article(shape.name)} ${shape.name} has exactly one line of symmetry — fold along it and the two halves land on top of each other.`
              : `${Article(shape.name)} ${shape.name} has ${shape.lines} lines of symmetry — fold along any of them and the two halves land on top of each other.`,
        hint: 'Imagine folding along the line. Do the two halves land exactly on top of each other?',
        steps: [
          { label: 'Try a vertical fold.', latex: '\\text{fold}' },
          { label: 'Then a horizontal one, then the diagonals.', latex: '\\text{fold}' },
          { label: 'Count only the folds that match exactly.', latex: String(shape.lines) },
        ],
        traps: [
          ...(diagonalsCounted !== shape.lines ? [trap(diagonalsCounted, 'diagonal-taken-as-symmetry')] : []),
          ...(shape.sides !== shape.lines && shape.sides !== diagonalsCounted
            ? [trap(shape.sides, 'symmetry-counted-by-sides')]
            : []),
        ],
      });
    },
  }),

  template({
    id: 'regular-polygon-symmetry',
    conceptId: 'symmetry',
    description: 'Count the lines of symmetry of a regular polygon.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const sides = difficulty === 2 ? rng.int(3, 7) : rng.int(8, 12);
      return numericExercise({
        prompt: `This regular polygon has ${sides} equal sides. How many lines of symmetry does it have?`,
        figure: { kind: 'polygon', sides, showCentre: true },
        value: sides,
        summary: `A regular polygon with ${sides} sides has ${sides} lines of symmetry — one through every vertex or every edge midpoint.`,
        hint: 'A regular shape looks the same from every corner. What does that tell you about the folds?',
        steps: [
          { label: 'Every vertex looks like every other.', latex: String(sides) },
          { label: 'So every fold through the centre works.', latex: String(sides) },
        ],
      });
    },
  }),

  template({
    id: 'which-shape-has-symmetry',
    conceptId: 'symmetry',
    description: 'Pick the shape with a given number of lines of symmetry.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const pool = SYMMETRY_SHAPES.filter((entry) => (difficulty === 3 ? entry.lines > 1 : entry.lines > 0));
      const target = rng.pick(pool);
      const others = rng.shuffle(SYMMETRY_SHAPES.filter((entry) => entry.lines !== target.lines)).slice(0, 3);
      const { choices, correctId } = options(
        [
          { label: target.name, correct: true },
          ...others.map((entry) => ({
            label: entry.name,
            misconceptionId: entry.lines === 0 ? 'diagonal-taken-as-symmetry' : 'symmetry-counted-by-sides',
          })),
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Which of these shapes has exactly ${target.lines} ${target.lines === 1 ? 'line' : 'lines'} of symmetry?`,
        choices,
        correctId,
        summary: `${Article(target.name)} ${target.name} has ${target.lines}. Counting sides, or counting every line through the centre, gives the wrong answer for the others.`,
        hint: 'A shape does not have one line of symmetry per side. Check each one by folding.',
        steps: [
          { label: 'Fold each candidate.', latex: '\\text{fold}' },
          { label: 'Only this one matches.', latex: String(target.lines) },
        ],
      });
    },
  }),
];
